import React, {useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import GTTask from "./GTTask";
import GTGridLayout from "./GTGridLayout";
import {ENTITY, resetTaskIdForScroll, setTaskIdForScroll} from "../../redux/slices/timeline";
import moment from "moment";
import {ViewType} from "./types";
import GTProject from "./GTProject";

export const GT_spacing = 75;
export const GT_taskCardHeight = 100;
export const GT_bottomSpacing = 50;
export const GT_progressBarTopInPx = 20;
export const GT_tasksTopInPx = 150 + GT_progressBarTopInPx;
export const GT_progressBarAndTasksGapInPx = 30;

export const getSpacing = (viewType: ViewType, containerWidth: number, startDate?: string) => {
  const totalMargin = 0; // can be adjusted in future
  const availableWidth = containerWidth - totalMargin;

  if (viewType === 'weekly') {
    return Math.round(availableWidth / 7);
  }
  if (viewType === 'monthly') {
    return Math.round(availableWidth / 24);
  }
  if (viewType === 'quarterly') {
    return Math.round(availableWidth / 69);
  }
  if (viewType === 'yearly') {
    const start = moment(startDate).startOf('year');
    const end = moment(startDate).endOf('year');
    const totalDays = end.diff(start, 'days') + 1;

    return Math.round(availableWidth / totalDays);
  }

  return GT_spacing;
};

interface GanttViewTimelineProps {
  simFlag: boolean;
  viewType: ViewType;
  orgId: string;
  onProjectClick: (project: any) => void;
  onTaskClick: (task: any) => void;
}

const GanttViewTimeline: React.FC<GanttViewTimelineProps> = ({
  orgId,
  simFlag = false,
  viewType = 'weekly',
  onProjectClick,
  onTaskClick
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    taskIdForScroll,
    projectIdForScroll,
    currentEntity,
    dashboardDetails,
    simulation
  } = useSelector((state: any) => state.timeline);

  const {
    data: tasks,
    singleRoadmapRes,
  } = useSelector((state: any) => state.roadmap);

  const calculateSpacing = () => {
    if (!containerRef.current) return GT_spacing;

    const startDate = currentEntity === ENTITY.ROADMAP
      ? singleRoadmapRes?.start_date
      : (tasks && tasks.length > 0 ? tasks[0]?.start : moment().format('YYYY-MM-DD'));

    return getSpacing(viewType, containerRef.current.offsetWidth, startDate);
  };

  const currentSpacing = calculateSpacing();

  const handleTaskClick = (task: any) => {
    if (task.type !== "project") {
      onTaskClick(task);

      dispatch(resetTaskIdForScroll())
      setTimeout(() => {
        dispatch(setTaskIdForScroll(`task__${tasks?.[0].id}__${task.id}`))
      }, 500)
    }
  };

  const getStartDate = () => {
    if(currentEntity === ENTITY.ROADMAP) {
      if(simulation) {
        return singleRoadmapRes?.sim_data?.start_date
      }

      return singleRoadmapRes?.start_date
    } else if (currentEntity === ENTITY.PROJECT) {

      return tasks[0]?.start
    }
  }

  const getEndDate = () => {
    if(currentEntity === ENTITY.ROADMAP) {
      if(simulation) {
        return singleRoadmapRes?.sim_data?.end_date
      }

      return singleRoadmapRes?.end_date
    } else if (currentEntity === ENTITY.PROJECT) {

      return tasks[0]?.end
    }
  }

  const scrollToTimelineItem = (scrollId: string, shouldBeDelayed: boolean = false) => {
    const scrollTimeMs = shouldBeDelayed ? 600 : 100;
    const highlightTimeMs = shouldBeDelayed ? 600 : 400;
    const highlightRemoveTimeMs = 3000;

    setTimeout(() => {
      const element = document.getElementById(scrollId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });

        setTimeout(() => {
          element.classList.add('timeline-item-highlight');
          const removeClass = () => {
            element.classList.remove('timeline-item-highlight');
          };
          element.addEventListener('animationend', removeClass, { once: true });
          setTimeout(removeClass, highlightRemoveTimeMs);
        }, highlightTimeMs);
      }
    }, scrollTimeMs);
  };

  useEffect(() => {
    const scrollId = currentEntity === ENTITY.PROJECT ? taskIdForScroll : projectIdForScroll;
    if (!scrollId) return;

    scrollToTimelineItem(scrollId);
  }, [projectIdForScroll, taskIdForScroll]);

  useEffect(() => {
    const scrollId = currentEntity === ENTITY.PROJECT ? taskIdForScroll : projectIdForScroll;
    if (!scrollId) return;

    scrollToTimelineItem(scrollId, true);
  }, [viewType]);

  useEffect(() => {
    const handler = (e: any) => {
      if (!tasks || tasks.length === 0) return;

      const detail = e?.detail || {};
      const rawTaskId = String(detail.taskId || '').trim();
      if (!rawTaskId) return;
      const list = tasks.slice(1);
      const found = list.find((t: any) => String(t.id) === rawTaskId || String(t.item_id) === rawTaskId);
      if (!found) return;

      dispatch(resetTaskIdForScroll());
      setTimeout(() => {
        dispatch(setTaskIdForScroll(`task__${tasks?.[0].id}__${found.id}`));
      }, 50);
    };

    window.addEventListener('cadencepro:locate-task', handler as EventListener);
    return () => window.removeEventListener('cadencepro:locate-task', handler as EventListener);
  }, [tasks]);

  return (
    <div
      ref={containerRef}
      className={`relative h-full w-full`}
    >
      <GTGridLayout
        orgId={orgId}
        viewType={viewType}
        spacing={currentSpacing}
        entityLength={currentEntity === ENTITY.ROADMAP ? singleRoadmapRes?.projects?.length : tasks.slice(1).length}
        startDate={getStartDate()}
        endDate={getEndDate()}
        milestones={currentEntity === ENTITY.ROADMAP ? singleRoadmapRes?.milestones : undefined}
      />

      <div className="relative" style={{top: GT_tasksTopInPx, width: 'fit-content'}}>
        {currentEntity === ENTITY.ROADMAP
          && singleRoadmapRes
          && singleRoadmapRes?.projects?.length > 0
          && singleRoadmapRes.projects.map((project: any, index: number) => (
          <div key={`project-${project?.project_id ?? project?.id ?? index}`}>
            <GTProject
              orgId={orgId}
              childEntityType={dashboardDetails.childEntityType}
              project={project}
              index={index}
              handleProjectClick={onProjectClick}
              startDate={singleRoadmapRes?.start_date}
              isTaskHighlighted={false}
              viewType={viewType}
              simFlag={simFlag}
              spacing={currentSpacing}
            />
          </div>
        ))}

        {currentEntity === ENTITY.PROJECT
          && tasks
          && tasks.length > 0
          && tasks.slice(1).map((task: any, index: number) => (
          <div key={`task-${task?.id ?? task?.task_id ?? index}`}>
            <GTTask
              orgId={orgId}
              task={task}
              index={index}
              handleTaskClick={handleTaskClick}
              startDate={tasks[0]?.start}
              isTaskHighlighted={false}
              viewType={viewType}
              spacing={currentSpacing}
            />
          </div>
        ))}
      </div>
    </div>
  )
};

export default GanttViewTimeline;